import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  ViewChild,
  Input,
  Output,
  OnInit,
  OnDestroy,
  Renderer2,
  Inject,
} from '@angular/core';
import { map, takeUntil } from 'rxjs/operators';
import { SessionService } from '@features/session/session.service';
import packageInfo from '../../../../../package.json';
import { MatMenuTrigger } from '@angular/material/menu';
import { Notification } from '@libs/notification-view/notification.model';
import { Router } from '@angular/router';
import { NavBarTourService } from './navbar-usertour.service';
import { ConfigService } from '@core/services/config.service';
import { CourseEnrollment } from '@features/dashboard/dashboard.model';
import { LoggedInUserRole } from '@features/session/session.model';
import { ConfirmationService } from '@core/services/confirmation.service';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input() visible = true;
  @Input() showBackButton = false;
  @Input() showHomeButton = false;
  @Input() showBadgeButton = false;
  @Input() showChapterHomeButton = false;
  @Input() hasSidenav = false;
  @Input() notifications: Notification[] = [];
  @Input() unreadNotificationsCount = 0;
  @Input() sessionStatus: CourseEnrollment[] = [];
  @Output() menuClicked = new EventEmitter<boolean>();
  @Output() backClicked = new EventEmitter<boolean>();
  @Output() homeClicked = new EventEmitter<boolean>();
  @Output() chapterHomeClicked = new EventEmitter<boolean>();
  @Output() notificationClicked = new EventEmitter<Notification>();
  @Output() allNotificationsRead = new EventEmitter<boolean>();
  @Output() notificationOpened = new EventEmitter<boolean>();
  @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;

  public version = packageInfo.version;
  private destroyed$ = new Subject<void>();

  constructor(
    private breakpointObserver: BreakpointObserver,
    private session: SessionService,
    private confirmService: ConfirmationService,
    private router: Router,
    private appConfig: ConfigService,
    private navbarUserTour: NavBarTourService,
    private renderer: Renderer2,
    private titleService: Title, // Inject Angular's Title service
    @Inject(DOCUMENT) private document: Document
  ) {}

  isMobile$ = this.breakpointObserver
    .observe(['(max-width: 820px)'])
    .pipe(map((result) => result.matches));

  ngOnInit(): void {
    // Set the document title on initialization based on the product flag.
    this.titleService.setTitle(this.appName);
    // Also update favicons.
    this.updateFavicons(this.isLumos);

    // Subscribe to session changes; when the session updates, refresh favicons and title.
    this.session.loggedIn$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.updateFavicons(this.isLumos);
      this.titleService.setTitle(this.appName);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Helper getter that reads the isLumos flag from session's orgDetails.
   * Defaults to false (Litmus Learn) if not provided.
   */
  get isLumos(): boolean {
    return this.session.isLumos;
  }

  /**
   * Returns the application name based on the product flag.
   * When isLumos is true, returns "Lumos", otherwise "Litmus Learn".
   */
  get appName(): string {
    return this.isLumos ? 'Lumos' : 'Litmus Learn';
  }

  /**
   * Update favicon and apple-touch-icon links based on the isLumos flag.
   * @param isLumos - Flag indicating which product's favicons to load.
   */
  private updateFavicons(isLumos: boolean): void {
    const faviconSvgPath = isLumos
      ? '/assets/favicon_lumos/favicon.svg'
      : '/assets/favicon/favicon.svg';

    const faviconPngPath = isLumos
      ? '/assets/favicon_lumos/favicon.png'
      : '/assets/favicon/favicon.png';

    const appleTouchIconPath = isLumos
      ? '/assets/favicon_lumos/apple-touch-icon.png'
      : '/assets/favicon/apple-touch-icon.png';

    // Update or create the SVG favicon link.
    const linkSvg: HTMLLinkElement = this.document.querySelector(
      'link[rel="icon"][type="image/svg+xml"]'
    );
    if (linkSvg) {
      this.renderer.setAttribute(linkSvg, 'href', faviconSvgPath);
    } else {
      const newLinkSvg = this.renderer.createElement('link');
      this.renderer.setAttribute(newLinkSvg, 'rel', 'icon');
      this.renderer.setAttribute(newLinkSvg, 'type', 'image/svg+xml');
      this.renderer.setAttribute(newLinkSvg, 'href', faviconSvgPath);
      this.renderer.appendChild(this.document.head, newLinkSvg);
    }

    // Update or create the PNG favicon link.
    const linkPng: HTMLLinkElement = this.document.querySelector(
      'link[rel="icon"][type="image/png"]'
    );
    if (linkPng) {
      this.renderer.setAttribute(linkPng, 'href', faviconPngPath);
    } else {
      const newLinkPng = this.renderer.createElement('link');
      this.renderer.setAttribute(newLinkPng, 'rel', 'icon');
      this.renderer.setAttribute(newLinkPng, 'type', 'image/png');
      this.renderer.setAttribute(newLinkPng, 'href', faviconPngPath);
      this.renderer.appendChild(this.document.head, newLinkPng);
    }

    // Update or create the apple-touch-icon link.
    let linkApple: HTMLLinkElement = this.document.querySelector(
      'link[rel="apple-touch-icon"]'
    );
    if (linkApple) {
      this.renderer.setAttribute(linkApple, 'href', appleTouchIconPath);
    } else {
      const newLinkApple = this.renderer.createElement('link');
      this.renderer.setAttribute(newLinkApple, 'rel', 'apple-touch-icon');
      this.renderer.setAttribute(newLinkApple, 'href', appleTouchIconPath);
      this.renderer.appendChild(this.document.head, newLinkApple);
    }
  }

  onClickMenu() {
    this.menuClicked.emit(true);
  }

  onClickBack() {
    this.backClicked.emit(true);
  }

  onClickHome() {
    if (this.isResource) {
      this.confirmService.confirmDialog(
        'End Session',
        'You can start a new session from the dashboard. Are you sure?',
        (next) => {
          if (next) {
            this.homeClicked.emit(true);
          }
        }
      );
    } else {
      this.homeClicked.emit(true);
    }
  }

  onClickChapterHome() {
    this.chapterHomeClicked.emit(true);
  }

  getStudentNames(): string {
    if (this.sessionStatus) {
      const studentList = this.sessionStatus
        .map((student) => `<li>${student.userName}</li>`)
        .join('');
      return `<ul>${studentList}</ul>`;
    }
    return '';
  }

  get userInitials() {
    if (!this.session?.isLoggedIn) {
      return 'A';
    }
    return this.session.userFirstName?.charAt(0) || 'A';
  }

  onClickUserTour() {
    this.navbarUserTour.handleUserTour(this.router.url);
  }

  get ssoOrgId() {
    return this.appConfig.readConfig().sso_orgId;
  }

  get organisation() {
    return this.session.organization;
  }

  get showOrgInfo() {
    return this.organisation !== this.ssoOrgId;
  }

  logout() {
    this.confirmService.confirmDialog(
      'Logout',
      'You will be logged out of your account. Are you sure?',
      (next) => {
        if (next) {
          this.session.logout();
        }
      }
    );
  }

  get userRole() {
    return this.session.userRole;
  }

  get isResource() {
    return this.userRole === LoggedInUserRole.RESOURCE;
  }

  get orgName() {
    if (this.session.stateSnapshot.orgDetails?.displayName) {
      return this.session.stateSnapshot.orgDetails.displayName;
    } else {
      this.session.getOrgDetails().subscribe((res) => {
        this.session.setOrgDetails(res);
      });
      return this.session.stateSnapshot.orgDetails?.displayName;
    }
  }

  handleNotificationClicked(notification: Notification) {
    this.notificationClicked.emit(notification);
  }

  handleMarkAllReadClicked() {
    this.allNotificationsRead.emit(true);
  }
}
